import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { newDependentPlans, newDependentCost } from "EmployeeDashboard/DependentApplication/Reducers";
import { watchRequestMedicalPlanUpdate } from "EmployeeDashboard/DependentApplication/Sagas";
import { watchRequestTerminateEmployeeDependent } from "EmployeeDashboard/Sagas";
import { ENTITY_ACTIONS } from "EntitiesApi/Actions";
import { entitiesReducer, type EntityTable } from "EntitiesApi/Reducers";
import { rootSaga as apiSaga } from "EntitiesApi/Sagas";
import { ENTITY_PATHS } from "EntitiesApi/Selectors";
import { beneficiaryPairsApi } from "Features/BeneficiaryPairs";
import { individualApplicationApi } from "IndividualApplication";
import { individualLifeApi } from "IndividualLife/api";
import { type Reducer } from "redux";
import honeybadgerMiddleware from "Redux/Middleware/honeybadgerMiddleware";
import { reducer as formReducer } from "redux-form";
import createSagaMiddleware from "redux-saga";
import { fork } from "redux-saga/effects";

import { abTestReducer } from "Common/abTestUtils/redux";
import { siteConfigSlice } from "Common/Redux/siteConfig";

import { accountId, initialDataLoaded } from "./reducers";

import { accountApi, smartlookMiddleware } from "Care/features/account";
import { chatApi } from "Care/features/chat";
import { costsSlice } from "Care/features/costs";
import { quoteApi } from "Care/features/quote";

import type { Account, Claim, EmployeeDependent, EmployeeForm, EmployeePolicy, Plan } from "Utils/types";

export type OriginalState = {
  accountId: string;
  entities: {
    accounts: EntityTable<Account>;
    claims: EntityTable<Claim>;
    employeeDependents: EntityTable<EmployeeDependent>;
    employeeForms: EntityTable<EmployeeForm>;
    employeePolicies: EntityTable<EmployeePolicy>;
    medicalPlans: EntityTable<Plan>;
  };
  initialDataLoaded: boolean;
  adminMode: boolean;
  employeeFormId: string;
  abTests: ReturnType<typeof abTestReducer>;
  form: ReturnType<typeof formReducer>;
  [accountApi.reducerPath]: ReturnType<typeof accountApi.reducer>;
  [costsSlice.name]: ReturnType<typeof costsSlice.reducer>;
};

export type Action = {
  readonly type: string;
};

const constantReducer = (initial) => (x) => (typeof x === "undefined" ? initial : x);

export const rootReducer = combineReducers({
  accountId,
  entities: entitiesReducer({
    employeeDependents: {},
    claims: {},
    employeeForms: {},
    employeePolicies: {},
    medicalPlans: {},
    dentalPlans: {},
    visionPlans: {},
  }) as Reducer<any>,
  initialDataLoaded,
  adminMode: constantReducer(false),
  employeeFormId: constantReducer(""),
  form: formReducer,
  abTests: abTestReducer,
  newDependentPlans,
  newDependentCost,
  [costsSlice.name]: costsSlice.reducer,
  [accountApi.reducerPath]: accountApi.reducer,
  [chatApi.reducerPath]: chatApi.reducer,
  [individualLifeApi.reducerPath]: individualLifeApi.reducer,
  [individualApplicationApi.reducerPath]: individualApplicationApi.reducer,
  [siteConfigSlice.name]: siteConfigSlice.reducer,
  [quoteApi.reducerPath]: quoteApi.reducer,
  [beneficiaryPairsApi.reducerPath]: beneficiaryPairsApi.reducer,
});
const sagaMiddleware = createSagaMiddleware();

function* rootSaga() {
  yield fork(apiSaga);
  yield fork(watchRequestTerminateEmployeeDependent);
  yield fork(watchRequestMedicalPlanUpdate);
}

const createStore = (initialState: Partial<ReturnType<typeof rootReducer>>) => {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: {
          ignoredPaths: ["form.submitClaims.values.claim.file"],
        },
        // New with RTK - builtin checks for type safety
        // Entities API works, but throws serializer errors due to this
        // Disabling the serializable check suppresses the benign error
        // Future: Migrate away from Entities API and towards RTK
        serializableCheck: {
          ignoredActions: [...ENTITY_ACTIONS],
          ignoredPaths: [...ENTITY_PATHS, "form.submitClaims.values.claim.file"],
        },
      })
        .concat(sagaMiddleware)
        .concat(honeybadgerMiddleware)
        .concat(accountApi.middleware)
        .concat(chatApi.middleware)
        .concat(individualLifeApi.middleware)
        .concat(individualApplicationApi.middleware)
        .concat(quoteApi.middleware)
        .concat(smartlookMiddleware)
        .concat(beneficiaryPairsApi.middleware),
    preloadedState: initialState,
  });

  // RTK automatically applies devtools - no need to add an enhancer

  sagaMiddleware.run(rootSaga);
  return store;
};

export default createStore;

export type Store = ReturnType<typeof createStore>;
export type State = ReturnType<ReturnType<typeof createStore>["getState"]>;
export type Dispatch = ReturnType<typeof createStore>["dispatch"];
